import React, { useEffect, useState, useCallback } from "react"
import { useSelector } from "react-redux"
import { useAuth0 } from "@auth0/auth0-react"
import StatsComponent from "../NurseAssignments/StatsComponent"
import NurseAcuityComponent from "../NurseAssignments/NurseAcuityComponent"
import useFetchSettings from "../../hooks/useFetchSettings"
import { getCurrentTime, getFirstOpenDateToday, getClinicTimes } from "../../utils/generalUtils"
import { currentlyNurseRealTimeOngoing } from "../../utils/realTimeNurseUtils"
import {
	currentlyRealTimeOngoing,
	currentlyScheduledOngoing,
	currentlyScheduledFuture30MinutesStarting,
	currentlyScheduledFuture30MinutesEnding,
	currentlyRealTimeFuture30MinutesStarting,
	currentlyRealTimeFuture30MinutesEnding
} from "../../utils/realTimeUtils"
import useFetchSchedules from "../../hooks/useFetchSchedules/useFetchSchedules"
import NavButtons from "./NavButtons"
import NurseLunchBreakComponent from "../NurseAssignments/NurseLunchBreakComponent"
import NurseLunchStatsComponent from "../NurseAssignments/NurseLunchStatsComponent"

function Cockpit({ sidebarOpen }) {
	const { user, isAuthenticated } = useAuth0()

	useFetchSchedules("Cockpit")

	// Select required data from the Redux store
	const patientSchedule = useSelector(state => state.schedule.patientSchedule)
	const nurseSchedule = useSelector(state => state.schedule.nurseSchedule)
	const { settingsData } = useFetchSettings()
	const testEnvironment = user.org_id === "org_lkg3WMhi3Q2l92iT"
	const centerOpen = settingsData?.centerOpen
	const centerClosedDates = settingsData?.center_closed_dates || []
	const today = getFirstOpenDateToday(centerOpen, testEnvironment, centerClosedDates)

	const { openTime, closeTime } = getClinicTimes(settingsData, today)

	const [currentTime, setCurrentTime] = useState(null)
	const [metrics, setMetrics] = useState({})
	const [nurseStats, setNurseStats] = useState({})
	const [viewMode, setViewMode] = useState("Nursing Acuity")

	const [viewScheduled, setViewScheduled] = useState(testEnvironment ? true : false)

	const currentMinute = getCurrentTime(false, false)

	const calculateMetrics = (resources, tasks) => {
		let totalScheduledAcuity = 0
		let totalActualAcuity = 0
		let nurseAcuityMapScheduled = {}
		let nurseAcuityMapActual = {}

		// Filter resources to include only nurses currently on shift
		const activeNurses = resources.filter(resource =>
			currentlyNurseRealTimeOngoing(resource, false, false)
		)

		activeNurses.forEach(resource => {
			nurseAcuityMapScheduled[resource.nurseId] = 0
			nurseAcuityMapActual[resource.nurseId] = 0
		})

		tasks.forEach(task => {
			if (!activeNurses.some(nurse => nurse.nurseId === task.assignedNurse)) {
				return
			}

			// Calculate scheduled acuity
			if (currentlyScheduledOngoing(task, false, false)) {
				totalScheduledAcuity += task.acuity
				nurseAcuityMapScheduled[task.assignedNurse] += task.acuity
			}

			// Calculate actual acuity
			if (currentlyRealTimeOngoing(task, false, false)) {
				totalActualAcuity += task.acuity
				nurseAcuityMapActual[task.assignedNurse] += task.acuity
			}
		})

		const totalNurses = activeNurses.length
		const averageScheduledAcuity = totalNurses > 0 ? totalScheduledAcuity / totalNurses : 0
		const averageActualAcuity = totalNurses > 0 ? totalActualAcuity / totalNurses : 0

		let nurseWithHighestWorkloadScheduled = "-"
		let nurseWithLowestWorkloadScheduled = "-"
		let highestWorkloadAcuityScheduled = 0
		let lowestWorkloadAcuityScheduled = 0

		let nurseWithHighestWorkloadActual = "-"
		let nurseWithLowestWorkloadActual = "-"
		let highestWorkloadAcuityActual = 0
		let lowestWorkloadAcuityActual = 0

		// Calculate highest and lowest workload based on scheduled acuity first
		const nurseIdWithHighestScheduledWorkload = Object.keys(nurseAcuityMapScheduled).reduce(
			(a, b) => {
				const totalScheduledAcuityA = nurseAcuityMapScheduled[a]
				const totalScheduledAcuityB = nurseAcuityMapScheduled[b]
				return totalScheduledAcuityA > totalScheduledAcuityB ? a : b
			},
			""
		)
		const nurseIdWithLowestScheduledWorkload = Object.keys(nurseAcuityMapScheduled).reduce(
			(a, b) => {
				const totalScheduledAcuityA = nurseAcuityMapScheduled[a]
				const totalScheduledAcuityB = nurseAcuityMapScheduled[b]
				return totalScheduledAcuityA < totalScheduledAcuityB ? a : b
			},
			""
		)

		// Now calculate highest and lowest workload based on actual acuity
		const nurseIdWithHighestActualWorkload = Object.keys(nurseAcuityMapActual).reduce(
			(a, b) => {
				const totalActualAcuityA = nurseAcuityMapActual[a]
				const totalActualAcuityB = nurseAcuityMapActual[b]
				return totalActualAcuityA > totalActualAcuityB ? a : b
			},
			""
		)
		const nurseIdWithLowestActualWorkload = Object.keys(nurseAcuityMapActual).reduce((a, b) => {
			const totalActualAcuityA = nurseAcuityMapActual[a]
			const totalActualAcuityB = nurseAcuityMapActual[b]
			return totalActualAcuityA < totalActualAcuityB ? a : b
		}, "")

		// Combine results to get the final names and acuities
		nurseWithHighestWorkloadScheduled =
			activeNurses.find(resource => resource.nurseId === nurseIdWithHighestScheduledWorkload)
				?.nurseName || "-"
		nurseWithLowestWorkloadScheduled =
			activeNurses.find(resource => resource.nurseId === nurseIdWithLowestScheduledWorkload)
				?.nurseName || "-"

		highestWorkloadAcuityScheduled =
			nurseAcuityMapScheduled[nurseIdWithHighestScheduledWorkload]
		lowestWorkloadAcuityScheduled = nurseAcuityMapScheduled[nurseIdWithLowestScheduledWorkload]

		nurseWithHighestWorkloadActual =
			activeNurses.find(resource => resource.nurseId === nurseIdWithHighestActualWorkload)
				?.nurseName || "-"
		nurseWithLowestWorkloadActual =
			activeNurses.find(resource => resource.nurseId === nurseIdWithLowestActualWorkload)
				?.nurseName || "-"

		highestWorkloadAcuityActual = nurseAcuityMapActual[nurseIdWithHighestActualWorkload]
		lowestWorkloadAcuityActual = nurseAcuityMapActual[nurseIdWithLowestActualWorkload]

		return {
			totalScheduledAcuity,
			averageScheduledAcuity,
			totalActualAcuity,
			averageActualAcuity,
			nurseWithHighestWorkloadScheduled,
			highestWorkloadAcuityScheduled,
			nurseWithLowestWorkloadScheduled,
			lowestWorkloadAcuityScheduled,
			nurseWithHighestWorkloadActual,
			highestWorkloadAcuityActual,
			nurseWithLowestWorkloadActual,
			lowestWorkloadAcuityActual
		}
	}

	const calculateIndividualNurseStats = (resources, tasks, sandbox, isTomorrowMode) => {
		// Filter resources to include only nurses currently on shift
		const activeNurses = resources.filter(resource =>
			currentlyNurseRealTimeOngoing(resource, false, false)
		)

		return activeNurses.map(resource => {
			// Declare variables for scheduled stats
			let currentScheduledAcuity = 0
			let currentScheduledPatients = 0
			let futureScheduledAcuity = 0

			// Declare variables for actual stats
			let currentActualAcuity = 0
			let currentActualPatients = 0
			let futureActualAcuity = 0

			tasks.forEach(task => {
				if (task.assignedNurse === resource.nurseId) {
					// Scheduled stats
					if (currentlyScheduledOngoing(task, sandbox, isTomorrowMode)) {
						currentScheduledAcuity += task.acuity
						futureScheduledAcuity += task.acuity
						currentScheduledPatients += 1
					}

					if (currentlyScheduledFuture30MinutesStarting(task, sandbox, isTomorrowMode)) {
						futureScheduledAcuity += task.acuity
					}

					if (currentlyScheduledFuture30MinutesEnding(task, sandbox, isTomorrowMode)) {
						futureScheduledAcuity -= task.acuity
					}

					// Actual stats

					if (currentlyRealTimeOngoing(task)) {
						// Current acuity for actual times
						currentActualAcuity += task.acuity
						currentActualPatients += 1
						futureActualAcuity += task.acuity
					}

					if (currentlyRealTimeFuture30MinutesStarting(task, sandbox, isTomorrowMode)) {
						futureActualAcuity += task.acuity
					}

					if (currentlyRealTimeFuture30MinutesEnding(task, sandbox, isTomorrowMode)) {
						futureActualAcuity -= task.acuity
					}
				}
			})

			const changeInScheduledAcuity = futureScheduledAcuity - currentScheduledAcuity

			const changeInActualAcuity = futureActualAcuity - currentActualAcuity

			return {
				nurseId: resource.nurseId,
				nurseName: resource.nurseName,
				// scheduled
				currentScheduledAcuity,
				currentScheduledPatients,
				futureScheduledAcuity,
				changeInScheduledAcuity,
				// actual
				currentActualAcuity,
				currentActualPatients,
				futureActualAcuity,
				changeInActualAcuity
			}
		})
	}

	useEffect(() => {
		const metricsData = calculateMetrics(nurseSchedule, patientSchedule)
		setMetrics(metricsData)
		const nurseStatsData = calculateIndividualNurseStats(
			nurseSchedule,
			patientSchedule,
			false,
			false
		)
		setNurseStats(nurseStatsData)
	}, [nurseSchedule, patientSchedule, currentTime])

	// Memoize updateTimePosition to prevent it from changing on every render
	const updateTimePosition = useCallback(() => {
		if (currentMinute >= openTime && currentMinute <= closeTime) {
			setCurrentTime(currentMinute)
		}
		// `currentMinute` is read each tick; capturing in deps would defeat memoization of the interval handler.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [openTime, closeTime]) // Add necessary dependencies

	useEffect(() => {
		updateTimePosition()
		const intervalId = setInterval(updateTimePosition, 60000)

		return () => clearInterval(intervalId)
	}, [openTime, closeTime, updateTimePosition])

	if (!isAuthenticated) {
		return null
	}

	const MAFNAT_on = settingsData?.toggleRules.MAFNAT

	const MAFNAT = MAFNAT_on ? settingsData?.maxAcuityForNurseAtAnyTime : 15

	const nurseStatsArray = Array.isArray(nurseStats) ? nurseStats : []

	// Determine the maximum acuity from the data
	const maxAcuity = Math.max(
		MAFNAT,
		...nurseStatsArray.map(nurse =>
			viewScheduled ? nurse.currentScheduledAcuity : nurse.currentActualAcuity
		)
	)

	const correctedMetrics = viewScheduled
		? {
				totalAcuity: metrics.totalScheduledAcuity,
				averageAcuity: metrics.averageScheduledAcuity,
				nurseWithHighestWorkload: metrics.nurseWithHighestWorkloadScheduled,
				nurseWithLowestWorkload: metrics.nurseWithLowestWorkloadScheduled,
				highestWorkloadAcuity: metrics.highestWorkloadAcuityScheduled,
				lowestWorkloadAcuity: metrics.lowestWorkloadAcuityScheduled
			}
		: {
				totalAcuity: metrics.totalActualAcuity,
				averageAcuity: metrics.averageActualAcuity,
				nurseWithHighestWorkload: metrics.nurseWithHighestWorkloadActual,
				nurseWithLowestWorkload: metrics.nurseWithLowestWorkloadActual,
				highestWorkloadAcuity: metrics.highestWorkloadAcuityActual,
				lowestWorkloadAcuity: metrics.lowestWorkloadAcuityActual
			}

	const acuityValues = {
		"Over Capacity": 0.6,
		"At Capacity": 0.4,
		"Near Capacity": 0.2,
		Available: 0.0
	}

	const getCapacityLabel = (acuity, nurse) => {
		const capacityLabels = {
			"Over Capacity": {
				threshold: acuityValues["Over Capacity"],
				className: "bg-red-200 text-red-800"
			},
			"At Capacity": {
				threshold: acuityValues["At Capacity"],
				className: "bg-orange-200 text-orange-800"
			},
			"Near Capacity": {
				threshold: acuityValues["Near Capacity"],
				className: "bg-yellow-200 text-yellow-800"
			},
			Available: {
				threshold: acuityValues["Available"],
				className: "bg-green-200 text-green-800"
			}
		}

		for (const [label, { threshold, className }] of Object.entries(capacityLabels)) {
			if (acuity / MAFNAT >= threshold) {
				return (
					<span
						className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${className} ml-3`}
					>
						{label}
					</span>
				)
			}
		}
	}

	const statusCounts = {
		available: 0,
		nearCapacity: 0,
		atCapacity: 0,
		overCapacity: 0
	}

	nurseStatsArray.forEach(nurse => {
		let acuityRatio
		if (viewScheduled) {
			acuityRatio = nurse.currentScheduledAcuity / MAFNAT
		} else {
			acuityRatio = nurse.currentActualAcuity / MAFNAT
		}

		if (acuityRatio >= acuityValues["Over Capacity"]) {
			statusCounts.overCapacity += 1
		} else if (acuityRatio >= acuityValues["At Capacity"]) {
			statusCounts.atCapacity += 1
		} else if (acuityRatio >= acuityValues["Near Capacity"]) {
			statusCounts.nearCapacity += 1
		} else {
			statusCounts.available += 1
		}
	})

	const firstName = user.name.split(" ")[0]

	if (!nurseStats) {
		// Render a loading indicator or return null until nurseStats is loaded
		return <div>Loading nurse data...</div>
	}

	return (
		<div>
			<div className={`${sidebarOpen ? "pl-64" : "pl-8"}`}>
				<div className="my-5 flex h-auto sm:h-16 flex-col sm:flex-row items-start sm:items-center justify-between bg-white px-4 sm:px-6 lg:px-8 print-hidden">
					<h1 className="font-bold text-4xl font-manrope text-custom-blue mb-4 sm:mb-0">
						Hello {firstName},
					</h1>
				</div>

				<div className="bg-white px-4 sm:px-6 lg:px-8 print-hidden">
					<div className="flex flex-wrap items-center gap-x-4 sm:gap-x-6">
						<NavButtons viewMode={viewMode} setViewMode={setViewMode} />
					</div>
				</div>

				<main className="">
					<div className="px-4 sm:px-6 lg:px-8">
						<div>
							{viewMode === "Nurse Lunch Break" ? (
								<>
									<div className="mt-4 mb-4">
										<NurseLunchStatsComponent nurseSchedule={nurseSchedule} />
									</div>
									<div className="mt-4 mb-4">
										<NurseLunchBreakComponent />
									</div>
								</>
							) : (
								<>
									<div className="mt-4 mb-4">
										<StatsComponent
											metrics={correctedMetrics}
											nurseStats={nurseStatsArray}
											maxAcuity={maxAcuity}
											statusCounts={statusCounts}
										/>
									</div>
									<div className="mt-4 mb-4">
										<NurseAcuityComponent
											nurseStats={nurseStatsArray}
											getCapacityLabel={getCapacityLabel}
											adjustedMAFNAT={MAFNAT * acuityValues["Over Capacity"]}
											maxAcuity={maxAcuity}
											viewScheduled={viewScheduled}
											setViewScheduled={setViewScheduled}
											nurseSchedule={nurseSchedule}
											patientSchedule={patientSchedule}
										/>
									</div>
								</>
							)}
						</div>
					</div>
				</main>
			</div>
		</div>
	)
}

export default Cockpit
